import React, { ReactNode } from 'react';

export const Navbar: React.FC<{ title: string; action?: ReactNode; children?: ReactNode }> = ({ title, action, children }) => (
  <header className="flex flex-col gap-3 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
    <div>
      <h1 className="text-page-header">{title}</h1>
      {children && <div className="mt-2 text-xs text-slate-500">{children}</div>}
    </div>
    {action && <div>{action}</div>}
  </header>
);

export const Sidebar: React.FC<{ items: ReactNode; footer?: ReactNode }> = ({ items, footer }) => (
  <aside className="flex h-full min-w-[260px] flex-col gap-4 rounded-3xl border border-slate-200 bg-slate-950 p-4 text-white shadow-lg">
    {items}
    {footer && <div className="mt-auto">{footer}</div>}
  </aside>
);

export const MobileBottomNav: React.FC<{ items: ReactNode }> = ({ items }) => (
  <nav className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-10px_30px_-20px_rgba(15,23,42,0.18)] backdrop-blur-sm sm:hidden">
    <div className="flex items-center justify-between gap-2">{items}</div>
  </nav>
);

export const DashboardCard: React.FC<{ title: string; value: string | number; detail?: string; badge?: string }> = ({ title, value, detail, badge }) => (
  <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-slate-500">{title}</p>
        <p className="mt-3 text-3xl font-semibold text-slate-950">{value}</p>
      </div>
      {badge && <span className="rounded-full bg-indigo-100 px-3 py-1 text-[11px] font-semibold text-indigo-700">{badge}</span>}
    </div>
    {detail && <p className="mt-4 text-sm text-slate-500">{detail}</p>}
  </div>
);

export const StatsCard: React.FC<{ title: string; value: string | number; trend?: string; icon?: ReactNode; variant?: 'blue' | 'emerald' | 'orange' | 'purple' }> = ({ title, value, trend, icon, variant = 'blue' }) => {
  const gradients = {
    blue: 'bg-gradient-to-br from-indigo-500 to-blue-600',
    emerald: 'bg-gradient-to-br from-emerald-500 to-teal-600',
    orange: 'bg-gradient-to-br from-orange-500 to-rose-500',
    purple: 'bg-gradient-to-br from-purple-500 to-fuchsia-600',
  };

  return (
    <div className={`rounded-3xl p-4 shadow-md text-white ${gradients[variant]}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-white/80">{title}</p>
          <p className="mt-3 text-2xl font-semibold text-white">{value}</p>
        </div>
        {icon && <div className="rounded-2xl bg-white/20 p-3 text-white">{icon}</div>}
      </div>
      {trend && <p className="mt-3 text-sm text-white/80">{trend}</p>}
    </div>
  );
};

export const TableShell: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="overflow-x-auto rounded-3xl border border-slate-200 bg-white shadow-sm">{children}</div>
);

export const TableHeader: React.FC<{ children: ReactNode }> = ({ children }) => (
  <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.3em] text-slate-500">{children}</thead>
);

export const TableRow: React.FC<{ children: ReactNode }> = ({ children }) => (
  <tr className="border-b border-slate-200 last:border-0 hover:bg-slate-50 transition-colors">{children}</tr>
);

export const DataFilter: React.FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
    <div className="mb-3 text-xs uppercase tracking-[0.3em] text-slate-500">{label}</div>
    {children}
  </div>
);

export const SearchInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <label className="relative block w-full min-w-[260px] text-slate-900">
    <span className="sr-only">Search</span>
    <input
      {...props}
      className="w-full rounded-3xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-base text-slate-900 shadow-sm outline-none transition-all focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20"
      placeholder={props.placeholder || 'Search'}
    />
    <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
      🔍
    </span>
  </label>
);

export const Modal: React.FC<{ open: boolean; title: string; onClose: () => void; children: ReactNode }> = ({ open, title, onClose, children }) => {
  if (!open) return null;
  return (
    <div className="modal-overlay">
      <div className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <button onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900">
            ✕
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

export const ConfirmDialog: React.FC<{ open: boolean; title: string; description: string; confirmLabel?: string; cancelLabel?: string; onCancel: () => void; onConfirm: () => void }> = ({ open, title, description, confirmLabel = 'Confirm', cancelLabel = 'Cancel', onCancel, onConfirm }) => (
  <Modal open={open} title={title} onClose={onCancel}>
    <p className="mb-6 text-sm text-slate-600">{description}</p>
    <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
      <button onClick={onCancel} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100">
        {cancelLabel}
      </button>
      <button onClick={onConfirm} className="rounded-2xl bg-rose-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-rose-700">
        {confirmLabel}
      </button>
    </div>
  </Modal>
);

export const StatusBadge: React.FC<{ label: string; variant?: 'success' | 'warning' | 'danger' | 'info' }> = ({ label, variant = 'info' }) => {
  const palette = {
    success: 'bg-emerald-100 text-emerald-700',
    warning: 'bg-amber-100 text-amber-700',
    danger: 'bg-rose-100 text-rose-700',
    info: 'bg-sky-100 text-sky-700',
  };
  return <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold ${palette[variant]}`}>{label}</span>;
};

export const ChartPanel: React.FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
  <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="mb-4 flex items-center justify-between gap-2">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
    </div>
    {children}
  </section>
);

export const NotificationToast: React.FC<{ message: string; type?: 'success' | 'error' | 'info' }> = ({ message, type = 'info' }) => {
  const palette = {
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    error: 'bg-rose-50 border-rose-200 text-rose-800',
    info: 'bg-sky-50 border-sky-200 text-sky-800',
  };
  return (
    <div className={`rounded-3xl border p-4 text-sm shadow-sm ${palette[type]}`}>
      {message}
    </div>
  );
};

export const Pagination: React.FC<{ currentPage: number; totalPages: number; onPageChange: (page: number) => void }> = ({ currentPage, totalPages, onPageChange }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
    <div>Page {currentPage} of {totalPages}</div>
    <div className="flex items-center gap-2">
      <button disabled={currentPage <= 1} onClick={() => onPageChange(currentPage - 1)} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50">Previous</button>
      <button disabled={currentPage >= totalPages} onClick={() => onPageChange(currentPage + 1)} className="rounded-2xl bg-indigo-600 px-3 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50">Next</button>
    </div>
  </div>
);

export const Tabs: React.FC<{ tabs: { label: string; active: boolean; onClick: () => void }[] }> = ({ tabs }) => (
  <div className="flex flex-wrap gap-2 rounded-3xl border border-slate-200 bg-slate-50 p-2">
    {tabs.map((tab) => (
      <button key={tab.label} onClick={tab.onClick} className={`rounded-3xl px-4 py-2 text-sm font-semibold ${tab.active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:bg-white/80'}`}>
        {tab.label}
      </button>
    ))}
  </div>
);

export const RoleChip: React.FC<{ label: string }> = ({ label }) => (
  <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-600">{label}</span>
);

export const ProfileCard: React.FC<{ name: string; role: string; email?: string; avatar?: string }> = ({ name, role, email, avatar }) => (
  <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex items-center gap-4">
      <div className="h-14 w-14 rounded-3xl bg-indigo-100 text-indigo-700 flex items-center justify-center text-xl font-bold">{avatar || name.charAt(0)}</div>
      <div>
        <h3 className="text-lg font-semibold text-slate-900">{name}</h3>
        <p className="text-sm text-slate-500">{role}</p>
        {email && <p className="text-sm text-slate-400">{email}</p>}
      </div>
    </div>
  </div>
);

export const AnalyticsWidget: React.FC<{ title: string; value: string | number; metric: string }> = ({ title, value, metric }) => (
  <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
    <p className="text-xs uppercase tracking-[0.3em] text-slate-500">{title}</p>
    <p className="mt-4 text-3xl font-semibold text-slate-950">{value}</p>
    <p className="mt-2 text-sm text-slate-500">{metric}</p>
  </div>
);

export const LoadingSkeleton: React.FC<{ lines?: number; className?: string }> = ({ lines = 4, className = "" }) => (
  <div className={`space-y-3 ${className}`}>
    {Array.from({ length: lines }).map((_, index) => {
      const widths = ['w-full', 'w-[92%]', 'w-[85%]', 'w-[95%]', 'w-[70%]'];
      return (
        <div 
          key={index} 
          className={`h-4 ${widths[index % widths.length]} rounded-full shimmer`} 
        />
      );
    })}
  </div>
);

export const TableSkeleton: React.FC<{ rows?: number; cols?: number; className?: string }> = ({ rows = 5, cols = 4, className = "" }) => (
  <div className={`overflow-x-auto rounded-3xl border border-slate-200 bg-white shadow-sm ${className}`}>
    <div className="p-5 bg-slate-50/50 border-b border-slate-200 flex justify-between items-center">
      <div className="h-4 w-1/4 rounded-full shimmer" />
      <div className="h-6 w-20 rounded-full shimmer" />
    </div>
    <table className="w-full text-left text-xs border-collapse">
      <thead>
        <tr className="bg-slate-50 border-b border-slate-200">
          {Array.from({ length: cols }).map((_, i) => (
            <th key={i} className="px-5 py-4">
              <div className="h-3 w-16 rounded-full shimmer" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/30">
            {Array.from({ length: cols }).map((_, c) => (
              <td key={c} className="px-5 py-4">
                <div className={`h-3.5 ${c === 0 ? 'w-28' : 'w-16'} rounded-full shimmer`} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const CardSkeleton: React.FC<{ count?: number; className?: string }> = ({ count = 3, className = "" }) => (
  <div className={`grid gap-5 sm:grid-cols-2 lg:grid-cols-3 ${className}`}>
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl shimmer" />
          <div className="space-y-2 flex-1">
            <div className="h-3 w-1/3 rounded-full shimmer" />
            <div className="h-4.5 w-2/3 rounded-full shimmer" />
          </div>
        </div>
        <div className="space-y-2 pt-3 border-t border-slate-100">
          <div className="h-3 w-5/6 rounded-full shimmer" />
          <div className="h-3 w-2/3 rounded-full shimmer" />
        </div>
      </div>
    ))}
  </div>
);

export const FormSkeleton: React.FC<{ fields?: number; className?: string }> = ({ fields = 3, className = "" }) => (
  <div className={`space-y-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm ${className}`}>
    <div className="h-5 w-1/4 rounded-full shimmer mb-4 border-b border-slate-100 pb-2" />
    <div className="grid gap-6 sm:grid-cols-2">
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-2">
          <div className="h-3 w-1/5 rounded-full shimmer" />
          <div className="h-10 w-full rounded-2xl shimmer" />
        </div>
      ))}
    </div>
    <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 mt-6">
      <div className="h-10 w-24 rounded-2xl shimmer" />
      <div className="h-10 w-28 rounded-2xl shimmer" />
    </div>
  </div>
);

export const ProfileSkeleton: React.FC = () => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-fade-in-up">
    {/* Avatar Sidebar Skeleton */}
    <div className="lg:col-span-4 space-y-6">
      <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden p-6 flex flex-col items-center">
        <div className="w-24 h-24 rounded-full shimmer mb-4" />
        <div className="h-5 w-2/3 rounded-full shimmer mb-2" />
        <div className="h-3.5 w-1/2 rounded-full shimmer mb-4" />
        <div className="h-6 w-20 rounded-full shimmer" />
        <div className="w-full border-t border-slate-100 mt-6 pt-6 space-y-4">
          <div className="h-3.5 w-1/3 rounded-full shimmer" />
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="p-3 bg-slate-50 rounded-2xl space-y-2">
                <div className="h-2.5 w-1/2 rounded-full shimmer" />
                <div className="h-3.5 w-3/4 rounded-full shimmer" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
    {/* Academic Results Tab Skeleton */}
    <div className="lg:col-span-8">
      <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden p-6 space-y-6">
        <div className="flex justify-between items-center border-b border-slate-100 pb-4">
          <div className="space-y-2">
            <div className="h-4 w-36 rounded-full shimmer" />
            <div className="h-3 w-48 rounded-full shimmer" />
          </div>
          <div className="h-6 w-20 rounded-full shimmer" />
        </div>
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="border border-slate-200 rounded-2xl p-5 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full shimmer" />
                <div className="space-y-2">
                  <div className="h-4 w-28 rounded-full shimmer" />
                  <div className="h-3 w-40 rounded-full shimmer" />
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="h-10 w-16 rounded-xl shimmer" />
                <div className="w-10 h-10 rounded-xl shimmer" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const AppLayoutSkeleton: React.FC = () => (
  <div className="flex h-screen w-screen bg-slate-50 p-6 md:p-8 gap-6 overflow-hidden">
    
    {/* Main content mock */}
    <div className="flex-1 flex flex-col min-w-0 gap-6">
      {/* Header mock */}
      <div className="h-20 w-full bg-white rounded-3xl border border-slate-200 px-6 flex items-center justify-between shrink-0 shadow-sm">
        <div className="h-4 w-40 rounded-full shimmer" />
        <div className="flex items-center gap-3">
          <div className="h-3 w-20 rounded-full shimmer" />
          <div className="w-9 h-9 rounded-full shimmer" />
        </div>
      </div>
      
      {/* Body mock */}
      <div className="flex-1 overflow-hidden space-y-6">
        <div className="grid gap-6 sm:grid-cols-3">
          <div className="h-32 rounded-3xl bg-white border border-slate-200 p-6 space-y-3 shadow-sm">
            <div className="h-3 w-12 rounded-full shimmer" />
            <div className="h-8 w-24 rounded-full shimmer" />
          </div>
          <div className="h-32 rounded-3xl bg-white border border-slate-200 p-6 space-y-3 shadow-sm">
            <div className="h-3 w-12 rounded-full shimmer" />
            <div className="h-8 w-24 rounded-full shimmer" />
          </div>
          <div className="h-32 rounded-3xl bg-white border border-slate-200 p-6 space-y-3 shadow-sm">
            <div className="h-3 w-12 rounded-full shimmer" />
            <div className="h-8 w-24 rounded-full shimmer" />
          </div>
        </div>
        <div className="flex-1 rounded-3xl bg-white border border-slate-200 p-6 space-y-4 shadow-sm min-h-[300px]">
          <div className="h-4 w-1/4 rounded-full shimmer" />
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-4 w-full rounded-full shimmer" />
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const EmptyState: React.FC<{ title: string; description: string; action?: ReactNode }> = ({ title, description, action }) => (
  <div className="rounded-3xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-600">
    <div className="mx-auto mb-6 h-16 w-16 rounded-full bg-slate-100 text-2xl leading-[4rem]">📭</div>
    <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
    <p className="mt-3 text-sm leading-relaxed text-slate-500">{description}</p>
    {action && <div className="mt-6">{action}</div>}
  </div>
);

// --- Expanded UI Form Components ---

export const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string }> = ({ label, error, ...props }) => (
  <div className="space-y-1 w-full">
    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">{label}</label>
    <input
      {...props}
      className={`w-full rounded-2xl border ${error ? 'border-rose-500 bg-rose-50/10' : 'border-slate-200 bg-slate-50/50'} px-4 py-2.5 text-xs text-slate-900 shadow-inner outline-none transition-all focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/10`}
    />
    {error && <span className="text-[9px] font-semibold text-rose-500">{error}</span>}
  </div>
);

export const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement> & { label: string; options: { value: string | number; label: string }[]; error?: string }> = ({ label, options, error, ...props }) => (
  <div className="space-y-1 w-full">
    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">{label}</label>
    <select
      {...props}
      className={`w-full rounded-2xl border ${error ? 'border-rose-500 bg-rose-50/10' : 'border-slate-200 bg-slate-50/50'} px-4 py-2.5 text-xs text-slate-900 outline-none transition-all focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/10 appearance-none`}
      style={{ backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E")`, backgroundPosition: 'right 1rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25rem' }}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    {error && <span className="text-[9px] font-semibold text-rose-500">{error}</span>}
  </div>
);

export const Textarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; error?: string }> = ({ label, error, ...props }) => (
  <div className="space-y-1 w-full">
    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">{label}</label>
    <textarea
      {...props}
      rows={props.rows || 3}
      className={`w-full rounded-2xl border ${error ? 'border-rose-500 bg-rose-50/10' : 'border-slate-200 bg-slate-50/50'} px-4 py-2.5 text-xs text-slate-900 shadow-inner outline-none transition-all focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/10`}
    />
    {error && <span className="text-[9px] font-semibold text-rose-500">{error}</span>}
  </div>
);

export const Switch: React.FC<React.InputHTMLAttributes<HTMLInputElement> & { label: string; description?: string }> = ({ label, description, ...props }) => (
  <label className="flex items-start justify-between gap-4 cursor-pointer py-1">
    <div className="space-y-0.5">
      <span className="text-xs font-bold text-slate-800">{label}</span>
      {description && <p className="text-[10px] text-slate-500 leading-normal">{description}</p>}
    </div>
    <div className="relative shrink-0 mt-1">
      <input type="checkbox" {...props} className="sr-only peer" />
      <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
    </div>
  </label>
);

// --- Grade Score Input (Touch / Blur optimized) ---

export const ScoreInput: React.FC<{ value: string; onChange: (v: string) => void; max?: number; status?: 'idle' | 'saving' | 'saved' | 'error' }> = ({ value, onChange, max = 100, status = 'idle' }) => {
  const isOutOfRange = value !== '' && (parseFloat(value) < 0 || parseFloat(value) > max);

  const statusIcons = {
    idle: null,
    saving: <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping absolute right-2.5 top-1/2 -translate-y-1/2" />,
    saved: <span className="text-[8px] text-emerald-500 font-bold absolute right-2 top-1/2 -translate-y-1/2">✓</span>,
    error: <span className="text-[8px] text-rose-500 font-bold absolute right-2 top-1/2 -translate-y-1/2">!</span>,
  };

  return (
    <div className="relative w-16">
      <input
        type="number"
        value={value}
        min={0}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        placeholder="-"
        className={`w-full text-center rounded-xl border py-1.5 pl-1.5 pr-4 text-xs font-bold transition-all outline-none ${
          isOutOfRange
            ? 'border-rose-500 bg-rose-50 text-rose-800 focus:ring-rose-500/20'
            : 'border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-600 focus:ring-indigo-500/20'
        }`}
      />
      {statusIcons[status]}
    </div>
  );
};

// --- Workflow / Step Tracker ---

export const ResultWorkflowTracker: React.FC<{ activeIndex: number }> = ({ activeIndex }) => {
  const steps = [
    { key: 'draft', label: 'Draft', desc: 'Entry open' },
    { key: 'submitted', label: 'Submitted', desc: 'Awaiting validation' },
    { key: 'validated', label: 'Validated', desc: 'Locked candidate' },
    { key: 'locked', label: 'Locked', desc: 'Finalized reports' }
  ];

  return (
    <div className="w-full py-4">
      <div className="flex items-center justify-between relative">
        {/* Track Line */}
        <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2 h-[3px] bg-slate-200 z-0">
          <div 
            className="h-full bg-indigo-600 transition-all duration-300"
            style={{ width: `${(activeIndex / (steps.length - 1)) * 100}%` }}
          />
        </div>

        {/* Steps */}
        {steps.map((step, idx) => {
          const isCompleted = idx < activeIndex;
          const isActive = idx === activeIndex;
          return (
            <div key={step.key} className="flex flex-col items-center relative z-10">
              <div 
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all duration-300 border-2 ${
                  isCompleted 
                    ? 'bg-indigo-600 border-indigo-600 text-white shadow-md' 
                    : isActive 
                      ? 'bg-white border-indigo-600 text-indigo-600 scale-110 shadow-lg' 
                      : 'bg-white border-slate-200 text-slate-400'
                }`}
              >
                {isCompleted ? '✓' : idx + 1}
              </div>
              <span className={`text-[9px] mt-1.5 font-bold ${isActive ? 'text-indigo-600 font-extrabold' : 'text-slate-500'}`}>
                {step.label}
              </span>
              <span className="text-[7px] text-slate-400 hidden sm:block mt-0.5">
                {step.desc}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- SVG Sparkline / Mini Trend Chart ---

export const Sparkline: React.FC<{ data: number[]; width?: number; height?: number; color?: string; fill?: boolean }> = ({ data, width = 120, height = 40, color = '#4f46e5', fill = true }) => {
  if (!data || data.length < 2) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min === 0 ? 1 : max - min;
  
  const points = data.map((val, idx) => {
    const x = (idx / (data.length - 1)) * width;
    const y = height - ((val - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');

  const fillPoints = `${points} ${width},${height} 0,${height}`;

  return (
    <svg width={width} height={height} className="overflow-visible">
      {fill && (
        <polygon
          points={fillPoints}
          fill="url(#sparkline-grad)"
          opacity="0.1"
        />
      )}
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
      
      {/* Gradients */}
      <defs>
        <linearGradient id="sparkline-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
};

// --- Telemetry Monitoring Checks ---

export const TelemetryGrid: React.FC = () => {
  const systems = [
    { name: 'Core API Server', status: 'Online', latency: '42ms', active: true },
    { name: 'Primary DB Replica', status: 'Healthy', latency: '4ms', active: true },
    { name: 'S3 Document Vault', status: 'Connected', latency: '128ms', active: true },
    { name: 'SMTP Delivery Agent', status: 'Idle', latency: '95%', active: true },
    { name: 'Redis Cache Cluster', status: 'Operational', latency: '1ms', active: true }
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 w-full">
      {systems.map((sys) => (
        <div key={sys.name} className="bg-white border border-slate-100 rounded-2xl p-3.5 shadow-sm hover:border-indigo-100 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-bold text-slate-800 truncate">{sys.name}</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          </div>
          <div className="mt-3 flex items-baseline justify-between gap-1">
            <span className="text-xs font-extrabold text-slate-900">{sys.status}</span>
            <span className="text-[8px] font-bold text-slate-400 uppercase">{sys.latency}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

